/**
 * The create-query form's draft (US-00-002).
 *
 * WHY A STORE AND NOT COMPONENT STATE. Two criteria need this to outlive the
 * form component:
 *
 *   - US-00-002.26: a lapsed session sends the user to OTP sign-in mid-submit and
 *     returns them to the screen they were on, with no partial or duplicate
 *     record created. The text has to still be there.
 *   - US-00-002.27: retrying after connectivity returns creates EXACTLY ONE
 *     query. That is only true if the retry reuses the first attempt's
 *     idempotency key, so the key belongs to the submission, not to the attempt.
 *
 * IT IS NOT PERSISTED. A notice description is the most sensitive text this app
 * handles, and localStorage is readable by any script on the origin; an in-memory
 * store survives the client-side navigation that the criteria actually describe.
 */

import { newIdempotencyKey } from "@anthra/platform/api"
import {
	type FileRejection,
	type PlatformFile,
	QUERY_ATTACHMENT_RULES,
	validateFileSet,
} from "@anthra/platform/files"
import { create } from "zustand"

type QueryDraftState = {
	title: string
	description: string
	/** Only files that passed the client-side rules. */
	attachments: PlatformFile[]
	/** One message per rejected file, naming the rule it broke (US-00-002.2). */
	rejections: FileRejection[]
	idempotencyKey: string | null

	setTitle: (value: string) => void
	setDescription: (value: string) => void
	/**
	 * Adds a picker's selection, KEEPING the files that pass.
	 *
	 * US-00-002.2 is explicit that a rejected fourth or oversized file leaves the
	 * others attached, and that the count is against what is already on the form —
	 * so the fourth file is the fourth even when it arrives in its own picker
	 * session.
	 */
	addFiles: (files: readonly PlatformFile[]) => void
	removeAttachment: (index: number) => void
	dismissRejections: () => void
	/** The submission's key, created on first use and stable until `reset`. */
	submissionKey: () => string
	reset: () => void
}

export const useQueryDraftStore = create<QueryDraftState>()((set, get) => ({
	title: "",
	description: "",
	attachments: [],
	rejections: [],
	idempotencyKey: null,

	setTitle: value => {
		set({ title: value })
	},

	setDescription: value => {
		set({ description: value })
	},

	addFiles: files => {
		const { attachments } = get()
		const result = validateFileSet(files, QUERY_ATTACHMENT_RULES, attachments.length)
		set({
			attachments: [...attachments, ...result.accepted],
			rejections: result.rejections,
		})
	},

	removeAttachment: index => {
		set(state => ({ attachments: state.attachments.filter((_, i) => i !== index) }))
	},

	dismissRejections: () => {
		set({ rejections: [] })
	},

	submissionKey: () => {
		const existing = get().idempotencyKey
		if (existing !== null) return existing
		const key = newIdempotencyKey()
		set({ idempotencyKey: key })
		return key
	},

	reset: () => {
		set({
			title: "",
			description: "",
			attachments: [],
			rejections: [],
			idempotencyKey: null,
		})
	},
}))
